import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, PushPin, PushPinOutlined, Search } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  integrationApiService,
  IntegrationApiError,
  IntegrationConfig,
  CustomAppThemeSectionLinks,
  CustomAppThemeSummary,
  MetafieldProvisionSummary,
  MotzuFieldPins,
  MotzuMetafieldField,
  ThemeBlockLink,
  ThemeBlocksResponse,
} from '../services/integrationApi';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';
import { useToast } from '../contexts/ToastContext';
import { FormDialog, InlineMessage, StatusChip } from './ui';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { tokens } from '../theme/tokens';
import { metafieldTypeLabel } from './mapping/mappingModel';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';

const PRESETS = COPY.storefrontPresets;
const SWITCH_COPY = PRESETS.fieldSwitches;

/** Governed by `overrideFields.weight`, not by the switch map. */
const SWITCH_EXEMPT_KEYS = new Set(['weight']);

type BucketId = 'all' | 'specs' | 'tables' | 'page' | 'ids' | 'pinned';

const SPEC_GROUPS = new Set([
  'Sensor / image',
  'Lens',
  'Autofocus',
  'Display',
  'Viewfinder',
  'Shutter / shooting',
  'Video',
  'Flash / exposure',
  'Connectivity / storage',
  'Capabilities',
  'Physical',
]);
const TABLE_KEYS = new Set(['specifications_html', 'specifications_json', 'key_specs_summary']);
const PAGE_KEYS = new Set([
  'features_list',
  'in_the_box',
  'documentation',
  'videos',
  'preorder',
  'preorder_availability_date',
  'preorder_message',
]);

const EXAMPLE = {
  title: 'Sony Alpha a7 IV Mirrorless Camera (Body Only)',
  values: {
    key_specs_summary: '33 MP | Full Frame | 4K',
    image_sensor_size: 'Full Frame',
    effective_pixels: '33 MP',
    lens_mount: 'Sony E',
    camera_hd_type: '4K',
    active_rebates: '$200 instant rebate through Oct 31',
    in_the_box: 'Battery, charger, strap, body cap, USB cable',
    instant_rebate: '200',
    mpn: 'ILCE7M4/B',
    iso_range: '100–51200',
    continuous_shooting_speed: '10 fps',
  } as Record<string, string>,
  specTable: [
    ['Sensor', '33 MP Full Frame Exmor R CMOS'],
    ['Lens mount', 'Sony E'],
    ['ISO', '100–51200'],
    ['Video', '4K 60p'],
    ['Stabilization', '5-axis in-body'],
  ],
};

const cardSx = {
  bgcolor: tokens.surface.card,
  border: '1px solid',
  borderColor: tokens.line.default,
  borderRadius: `${tokens.radius.panel}px`,
  boxShadow: tokens.shadow.card,
} as const;

const fieldId = (field: { namespace: string; key: string }) => `${field.namespace}.${field.key}`;

const inBucket = (field: MotzuMetafieldField, bucket: BucketId): boolean => {
  if (bucket === 'all') return true;
  if (bucket === 'pinned') return field.pinned;
  if (bucket === 'tables') return TABLE_KEYS.has(field.key);
  if (bucket === 'page') return PAGE_KEYS.has(field.key);
  if (bucket === 'ids') return field.group === 'Identifiers' || field.group === 'Pricing / promotions';
  return SPEC_GROUPS.has(field.group);
};

const hasIssues = (summary?: MetafieldProvisionSummary) =>
  (summary?.typeConflicts?.length ?? 0) > 0 || (summary?.failed?.length ?? 0) > 0;

export interface StorefrontPresetsCardProps {
  integrationId: string;
  /** When fields already exist, a previous sync counts as step 2. */
  lastSync?: string | null;
  /** `settings.motzuMetafields` of the integration (flag `shopify_metafield_switches`): `false` = off, missing = on. */
  motzuMetafields?: Record<string, boolean>;
  /** Called with the saved map so a parent holding its own copy of the integration settings stays current. */
  onMotzuMetafieldsSaved?: (map: Record<string, boolean>) => void;
  /** The integration uses the store's own Shopify app (`credentials.customApp`). */
  customApp?: boolean;
}

type SectionLinksState = CustomAppThemeSectionLinks & { metafieldWarning?: string };

const SECTION_COPY = 'integrations.customAppThemeSections';

function linksFromDetails(details: Record<string, unknown> | undefined): SectionLinksState | null {
  if (!details) return null;
  const { themeId, themeName, editorUrl, previewUrl } = details;
  if (typeof themeId !== 'string' || typeof themeName !== 'string' || typeof editorUrl !== 'string' || typeof previewUrl !== 'string') {
    return null;
  }
  return { themeId, themeName, editorUrl, previewUrl };
}

/**
 * Jordan sets up Motzu's own Shopify fields: create the definitions, sync
 * so they fill, then open the theme editor for the blocks.
 */
const StorefrontPresetsCard: React.FC<StorefrontPresetsCardProps> = ({ integrationId, lastSync, motzuMetafields, onMotzuMetafieldsSaved, customApp }) => {
  const copy = PRESETS;
  const { showToast } = useToast();
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [bucket, setBucket] = useState<BucketId>('all');
  const [query, setQuery] = useState('');
  const [exampleView, setExampleView] = useState<'admin' | 'page'>('admin');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [provisionSummary, setProvisionSummary] = useState<MetafieldProvisionSummary | undefined>();
  const [provisionedThisSession, setProvisionedThisSession] = useState(false);
  // Set by any provision that returned, clean or partial; the summary itself is cleared when the dialog closes.
  const [provisionRan, setProvisionRan] = useState(false);
  const [syncedThisSession, setSyncedThisSession] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [themeLoading, setThemeLoading] = useState(false);
  const [themeBlocks, setThemeBlocks] = useState<ThemeBlockLink[]>([]);
  const [themePayload, setThemePayload] = useState<ThemeBlocksResponse | null>(null);
  const [themeRequiresDefs, setThemeRequiresDefs] = useState(false);
  const [themeRequiresOs2, setThemeRequiresOs2] = useState(false);
  const [themeNotConfigured, setThemeNotConfigured] = useState(false);
  const [sectionLinks, setSectionLinks] = useState<SectionLinksState | null>(null);
  const [sectionThemes, setSectionThemes] = useState<CustomAppThemeSummary[]>([]);
  const [selectedThemeId, setSelectedThemeId] = useState('');
  const [sectionBusy, setSectionBusy] = useState<null | 'install' | 'update' | 'download' | 'another'>(null);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [scopesBlocked, setScopesBlocked] = useState(false);
  const { enabled: sectionsFlagOn } = useOrgFeatureFlag('shopify_custom_app_theme_sections');
  const sectionsFlow = customApp === true && sectionsFlagOn;

  const fieldsQuery = useQuery({
    queryKey: ['motzu-metafield-fields'],
    queryFn: () => integrationApiService.getMotzuMetafieldFields(),
    staleTime: Infinity,
  });
  const definitionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', integrationId],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(integrationId),
    staleTime: 5 * 60 * 1000,
  });

  const { enabled: pinningOn } = useOrgFeatureFlag('shopify_field_pinning');
  const { enabled: switchesOn } = useOrgFeatureFlag('shopify_metafield_switches');
  const [switchState, setSwitchState] = useState<Record<string, boolean>>(motzuMetafields || {});
  const [switchSaving, setSwitchSaving] = useState<string | null>(null);
  useEffect(() => {
    setSwitchState(motzuMetafields || {});
  }, [motzuMetafields]);
  const isFieldOn = (field: { key: string }) => switchState[field.key] !== false;
  const offCount = Object.entries(switchState).filter(([key, on]) => on === false && !SWITCH_EXEMPT_KEYS.has(key)).length;
  const toggleSwitch = async (field: MotzuMetafieldField) => {
    if (switchSaving) return;
    const previous = switchState;
    const next = { ...switchState, [field.key]: !isFieldOn(field) };
    setSwitchState(next);
    setSwitchSaving(field.key);
    try {
      await integrationApiService.updateIntegration(integrationId, { settings: { motzuMetafields: next } } as Partial<IntegrationConfig>);
      queryClient.invalidateQueries({ queryKey: ['integration', integrationId] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onMotzuMetafieldsSaved?.(next);
      showToast(SWITCH_COPY.saved, 'success');
    } catch (error) {
      setSwitchState(previous);
      showToast(error instanceof Error ? error.message : SWITCH_COPY.saveFailed, 'error');
    } finally {
      setSwitchSaving(null);
    }
  };
  const pinsQueryKey = ['shopify-field-pins', integrationId];
  const [pinningKey, setPinningKey] = useState<string | null>(null);

  const catalogFields = fieldsQuery.data || [];
  const createdKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const definition of definitionsQuery.data?.definitions || []) keys.add(fieldId(definition));
    return keys;
  }, [definitionsQuery.data]);

  const createdCount = catalogFields.filter((field) => createdKeys.has(fieldId(field))).length;
  const fieldsCreated = catalogFields.length > 0 && createdCount === catalogFields.length;
  // A partial provision still created fields, so it unlocks Sync; step 1 then offers a retry for the rest.
  const partlyCreated = provisionRan && createdCount > 0 && !fieldsCreated;
  const step1Done = fieldsCreated || (provisionedThisSession && !hasIssues(provisionSummary)) || partlyCreated;
  const step2Done = step1Done && (syncedThisSession || (Boolean(lastSync) && !provisionedThisSession));

  // With pinning on, pins are read live from the store once any field exists;
  // otherwise the list shows the default set Motzu pins on create.
  const pinsQuery = useQuery({
    queryKey: pinsQueryKey,
    queryFn: () => integrationApiService.getFieldPins(integrationId),
    enabled: pinningOn && createdCount > 0,
    staleTime: 60 * 1000,
  });
  const livePins = pinningOn ? pinsQuery.data : undefined;
  const fields = useMemo(() => {
    if (!livePins) return catalogFields;
    const pinned = new Set(livePins.pinnedKeys);
    return catalogFields.map((field) => ({ ...field, pinned: pinned.has(fieldId(field)) }));
  }, [catalogFields, livePins]);
  const pinnedCount = fields.filter((field) => field.pinned).length;
  const atPinLimit = Boolean(livePins && livePins.storePinnedCount >= livePins.limit);
  const pinnable = (field: MotzuMetafieldField) =>
    livePins ? !livePins.missingKeys.includes(fieldId(field)) : createdKeys.has(fieldId(field));

  const togglePin = async (field: MotzuMetafieldField) => {
    const key = fieldId(field);
    if (pinningKey) return;
    setPinningKey(key);
    try {
      const pins = await integrationApiService.setFieldPin(integrationId, key, !field.pinned);
      queryClient.setQueryData<MotzuFieldPins>(pinsQueryKey, pins);
    } catch (error) {
      showToast((error as Error).message, 'error');
      queryClient.invalidateQueries({ queryKey: pinsQueryKey });
    } finally {
      setPinningKey(null);
    }
  };

  const pinTooltip = (field: MotzuMetafieldField): string => {
    if (!livePins) {
      return field.pinned
        ? 'Shown in Shopify Admin'
        : 'Not shown in Shopify Admin. Motzu pins its default set when it creates the fields. Shopify allows up to 20.';
    }
    if (!pinnable(field)) return 'Create the fields first to pin this one.';
    if (field.pinned) return 'Shown in Shopify Admin · click to unpin';
    if (atPinLimit) {
      return `Shopify allows ${livePins.limit} pinned product fields and this store has ${livePins.storePinnedCount}. Unpin one first.`;
    }
    return 'Pin to show in Shopify Admin';
  };

  const buckets: Array<{ id: BucketId; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'specs', label: 'Specs' },
    { id: 'tables', label: 'Spec tables' },
    { id: 'page', label: 'Page content' },
    { id: 'ids', label: 'IDs & pricing' },
    { id: 'pinned', label: 'Pinned' },
  ];

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return fields.filter((field) => {
      if (!inBucket(field, bucket)) return false;
      if (!q) return true;
      return `${field.name} ${field.key} ${field.description}`.toLowerCase().includes(q);
    });
  }, [fields, bucket, query]);

  const groups = useMemo(() => {
    const byGroup = new Map<string, MotzuMetafieldField[]>();
    for (const field of visible) {
      const list = byGroup.get(field.group) || [];
      list.push(field);
      byGroup.set(field.group, list);
    }
    return Array.from(byGroup.entries());
  }, [visible]);

  const pinnedExamples = fields.filter((field) => field.pinned && EXAMPLE.values[field.key]);

  const closeConfirm = () => {
    if (provisioning) return;
    setConfirmOpen(false);
    setProvisionError(null);
    setProvisionSummary(undefined);
  };

  const handleProvision = async () => {
    if (provisioning) return;
    if (provisionSummary && hasIssues(provisionSummary)) {
      closeConfirm();
      return;
    }
    setProvisioning(true);
    setProvisionError(null);
    try {
      const result = await integrationApiService.provisionMetafieldDefinitions(integrationId);
      setProvisionSummary(result.summary);
      setProvisionRan(true);
      // Always re-read store definitions so created / already-present keys update
      // step1Done even when typeConflicts or failed kept the dialog open.
      queryClient.invalidateQueries({ queryKey: ['shopify-spec-metafield-options', integrationId] });
      queryClient.invalidateQueries({ queryKey: pinsQueryKey });
      if (hasIssues(result.summary)) {
        showToast(result.message, 'warning');
      } else {
        setProvisionedThisSession(true);
        showToast(result.message, 'success');
        setConfirmOpen(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to provision metafield definitions';
      setProvisionError(message);
      showToast(message, 'error');
    } finally {
      setProvisioning(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await integrationApiService.syncIntegration(integrationId);
      setSyncedThisSession(true);
      showToast('Sync started. It fills the fields on every product.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to start sync', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const applySectionError = (err: unknown, fallback: string) => {
    const message = err instanceof Error ? err.message : fallback;
    if (err instanceof IntegrationApiError && err.code === 'theme_scopes_required') setScopesBlocked(true);
    const recovered = err instanceof IntegrationApiError ? linksFromDetails(err.details) : null;
    if (recovered) setSectionLinks(recovered);
    setSectionError(message);
    showToast(message, 'error');
  };

  const handleThemeLinks = async () => {
    setThemeLoading(true);
    setSectionError(null);
    setSectionLinks(null);
    setScopesBlocked(false);
    setSectionThemes([]);
    setSelectedThemeId('');
    try {
      const data = await integrationApiService.getThemeBlocks(integrationId);
      setThemePayload(data);
      setThemeBlocks(data.blocks || []);
      setThemeRequiresDefs(data.requiresMetafieldDefinitions === true);
      setThemeRequiresOs2(data.requiresOnlineStore2Theme === true);
      setThemeNotConfigured(data.unavailableReason === 'not_configured');
      const info = data.customAppSections;
      if (info?.install) setSectionLinks(info.install);
      if (info?.scopesGranted === false) setScopesBlocked(true);
      setThemeOpen(true);
      if (data.mode === 'custom_app_sections' && info?.scopesGranted !== false) {
        try {
          const listed = await integrationApiService.listCustomAppThemes(integrationId);
          setSectionThemes(listed);
          const preferred = listed.find((theme) => theme.installed) || listed.find((theme) => theme.id === info?.install?.themeId) || listed[0];
          setSelectedThemeId(preferred?.id || info?.install?.themeId || '');
        } catch (err) {
          if (err instanceof IntegrationApiError && err.code === 'theme_scopes_required') setScopesBlocked(true);
        }
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load theme editor links', 'error');
    } finally {
      setThemeLoading(false);
    }
  };

  const handleInstallSections = async (force: boolean) => {
    if (scopesBlocked) return;
    setSectionBusy(force ? 'another' : 'install');
    setSectionError(null);
    try {
      const result = await integrationApiService.installCustomAppThemeSections(integrationId, force ? { force: true } : {});
      setSectionLinks(result);
      showToast(t(`${SECTION_COPY}.installed`, { theme: result.themeName }), 'success');
    } catch (err) {
      applySectionError(err, t(`${SECTION_COPY}.create`));
    } finally {
      setSectionBusy(null);
    }
  };

  const handleUpdateSections = async () => {
    const themeId = selectedThemeId || sectionLinks?.themeId;
    if (!themeId) return;
    setSectionBusy('update');
    setSectionError(null);
    try {
      const result = await integrationApiService.updateCustomAppThemeSections(integrationId, { themeId });
      setSectionLinks(result);
      setThemePayload((prev) => prev?.customAppSections
        ? { ...prev, customAppSections: { ...prev.customAppSections, updateAvailable: false } }
        : prev);
      showToast(t(`${SECTION_COPY}.upToDate`), 'success');
    } catch (err) {
      applySectionError(err, t(`${SECTION_COPY}.update`));
    } finally {
      setSectionBusy(null);
    }
  };

  const handleDownloadSections = async () => {
    setSectionBusy('download');
    try {
      await integrationApiService.downloadCustomAppThemeSections(integrationId);
    } catch (err) {
      applySectionError(err, t(`${SECTION_COPY}.download`));
    } finally {
      setSectionBusy(null);
    }
  };

  const stepState = (done: boolean, active: boolean) => {
    if (done) return { mark: '✓', dotBg: tokens.signal.positive.solid, dotInk: tokens.ink.inverse, titleInk: tokens.ink.primary, stateInk: tokens.signal.positive.ink, border: tokens.line.default, bg: tokens.surface.card };
    if (active) return { mark: '', dotBg: tokens.accent.main, dotInk: tokens.ink.inverse, titleInk: tokens.ink.primary, stateInk: tokens.accent.main, border: tokens.accent.border, bg: tokens.signal.info.row };
    return { mark: '', dotBg: tokens.surface.tint, dotInk: tokens.ink.faint, titleInk: tokens.ink.muted, stateInk: tokens.ink.faint, border: tokens.line.default, bg: tokens.surface.card };
  };

  const steps = [
    {
      n: '1',
      title: 'Create fields in Shopify',
      body: partlyCreated
        ? `${createdCount} of ${fields.length} fields created. The rest were skipped; review and create again to retry them.`
        : step1Done
          ? `${fields.length || createdCount} fields created. Existing fields with the same key were kept.`
          : 'Adds the empty field labels. You review before anything is written.',
      ...stepState(step1Done, !step1Done),
      state: partlyCreated ? 'Partly done' : step1Done ? 'Done' : 'Start here',
              action: partlyCreated ? 'Retry' : step1Done ? undefined : 'Review & create',
              onClick: () => {
                setProvisionError(null);
                setProvisionSummary(undefined);
                setConfirmOpen(true);
              },
              disabled: fieldsQuery.isLoading || fields.length === 0,
    },
    {
      n: '2',
      title: 'Sync products',
      body: 'The next sync fills the fields on every product.',
      ...stepState(step2Done, step1Done && !step2Done),
      state: step2Done ? 'Done' : step1Done ? 'Next' : 'Waiting',
      action: step1Done && !step2Done ? (syncing ? 'Syncing…' : 'Sync now') : undefined,
      onClick: handleSync,
      disabled: syncing,
    },
    {
      n: '3',
      title: sectionsFlow ? t(`${SECTION_COPY}.stepTitle`) : 'Add Motzu blocks to your theme',
      body: sectionsFlow ? t(`${SECTION_COPY}.stepBody`) : 'Add key specs, spec table and in-the-box blocks, then Save in Shopify.',
      ...stepState(false, step2Done),
      state: step2Done ? 'Your turn' : 'Waiting',
      action: step2Done ? (themeLoading ? (sectionsFlow ? t(`${SECTION_COPY}.opening`) : 'Opening…') : (sectionsFlow ? t(`${SECTION_COPY}.stepAction`) : 'Open theme editor')) : undefined,
      onClick: handleThemeLinks,
      disabled: themeLoading,
    },
  ];

  let submitLabel = 'Create fields';
  if (provisionSummary && hasIssues(provisionSummary)) submitLabel = copy.guard.close;
  else if (provisioning) submitLabel = 'Creating…';
  else if (provisionError) submitLabel = copy.guard.retry;

  const sectionsMode = themePayload?.mode === 'custom_app_sections';
  const customAppBlocked = themePayload?.mode === 'custom_app' || themePayload?.unavailableReason === 'custom_app';
  const scopesMissing = scopesBlocked || themePayload?.customAppSections?.scopesGranted === false;
  const hasSectionCopy = Boolean(sectionLinks);
  const updateThemeId = selectedThemeId || sectionLinks?.themeId || '';
  let themeTitle: string = copy.themeEditor.title;
  let themeSubmit: string = 'Close';
  let themeHideCancel = true;
  let themeOnSubmit = () => setThemeOpen(false);
  let themeSubmitLoading = false;
  if (sectionsMode && !scopesMissing && !hasSectionCopy) {
    themeTitle = t(`${SECTION_COPY}.dialogTitle`);
    themeSubmit = sectionBusy === 'install' ? t(`${SECTION_COPY}.creating`) : t(`${SECTION_COPY}.create`);
    themeHideCancel = false;
    themeOnSubmit = () => { void handleInstallSections(false); };
    themeSubmitLoading = sectionBusy === 'install';
  } else if (sectionsMode) {
    themeTitle = t(`${SECTION_COPY}.dialogTitle`);
  }

  return (
    <>
      <Box sx={{ ...cardSx, px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Product page fields</Typography>
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, maxWidth: 760, mt: 0.5 }}>
            Shopify has no place for camera specs, what&apos;s in the box or rebates. Motzu adds {fields.length || '…'} fields for them, fills them on every sync, and your theme shows them through Motzu blocks.
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(auto-fit, minmax(240px, 1fr))' }, gap: 1.25 }}>
          {steps.map((step) => (
            <Box key={step.n} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.75, borderRadius: '12px', border: '1px solid', borderColor: step.border, bgcolor: step.bg }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: step.dotBg, color: step.dotInk, fontSize: 12, fontWeight: 700, flex: '0 0 auto' }}>
                  {step.mark === '✓' ? <Check sx={{ fontSize: 14 }} /> : step.n}
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: step.titleInk }}>{step.title}</Typography>
                <Typography sx={{ ml: 'auto', fontSize: 11.5, fontWeight: 600, color: step.stateInk }}>{step.state}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{step.body}</Typography>
              {step.action && (
                <Button
                  variant="outlined"
                  disabled={step.disabled}
                  onClick={step.onClick}
                  sx={{ alignSelf: 'flex-start', height: 34, borderRadius: '9px', fontWeight: 600, borderColor: tokens.line.default, color: tokens.ink.body }}
                >
                  {step.action}
                </Button>
              )}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
          {sectionsFlow
            ? t(`${SECTION_COPY}.liveUntouched`)
            : 'Nothing here changes your live store on its own. You confirm field setup here, and blocks only go live when you save the theme in Shopify.'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ ...cardSx, flex: '2 1 560px', minWidth: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', px: 2.25, pt: 2, pb: 1.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Fields Motzu adds</Typography>
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
              {fields.length} fields · {pinnedCount} shown in Shopify Admin{switchesOn && offCount > 0 ? ` · ${SWITCH_COPY.offCount(offCount)}` : ''}
            </Typography>
          </Box>
          {switchesOn && (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, px: 2.25, pb: 1.25 }}>
              {SWITCH_COPY.intro} {SWITCH_COPY.weightNote}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', px: 2.25, pb: 1.5 }}>
            {buckets.map((item) => {
              const count = fields.filter((field) => inBucket(field, item.id)).length;
              const active = bucket === item.id;
              return (
                <Button
                  key={item.id}
                  onClick={() => setBucket(item.id)}
                  sx={{
                    height: 30,
                    minWidth: 0,
                    px: 1.25,
                    borderRadius: '8px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    bgcolor: active ? tokens.surface.header : tokens.surface.card,
                    color: active ? tokens.ink.inverse : tokens.ink.body,
                    border: '1px solid',
                    borderColor: active ? tokens.surface.header : tokens.line.default,
                  }}
                >
                  {item.label}
                  <Box component="span" sx={{ ml: 0.75, color: active ? tokens.ink.onDark : tokens.ink.faint }}>{count}</Box>
                </Button>
              );
            })}
            <TextField
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search, e.g. mount or rebate"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ flex: '1 1 180px', '& .MuiInputBase-root': { height: 32 } }}
            />
          </Box>
          <Box sx={{ maxHeight: 720, overflow: 'auto', borderTop: '1px solid', borderColor: tokens.line.hairline }}>
            {fieldsQuery.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
            )}
            {fieldsQuery.isError && (
              <Typography sx={{ px: 2, py: 2, fontSize: 13.5, color: tokens.signal.problem.ink }}>
                Could not load the field list. Try again in a moment.
              </Typography>
            )}
            {!fieldsQuery.isLoading && groups.length === 0 && (
              <Typography sx={{ px: 2, py: 2, fontSize: 13.5, color: tokens.ink.muted }}>No field matches that search.</Typography>
            )}
            {groups.map(([group, list]) => (
              <Box key={group}>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', height: 30, px: 2, bgcolor: tokens.surface.sunken }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                    {group}
                  </Typography>
                  <Typography sx={{ ml: 1, fontSize: 11, color: tokens.ink.faint }}>{list.length}</Typography>
                </Box>
                {list.map((field) => (
                  <Box
                    key={field.key}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: switchesOn
                        ? { xs: 'minmax(0,1fr) auto auto 32px', md: '1fr 1.35fr auto auto 32px' }
                        : { xs: 'minmax(0,1fr) auto 32px', md: '1fr 1.35fr auto 32px' },
                      gap: 1.5,
                      alignItems: 'center',
                      px: 2,
                      py: 1.25,
                      borderTop: '1px solid',
                      borderColor: tokens.line.hairline,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {createdKeys.has(fieldId(field)) && (
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.signal.positive.solid, flex: '0 0 auto' }} />
                        )}
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{field.name}</Typography>
                        <Tooltip title={t(field.tier === 'custom' ? 'integrations.specTier.customHint' : 'integrations.specTier.canonicalHint')}>
                          <span>
                            <StatusChip
                              label={t(field.tier === 'custom' ? 'integrations.specTier.custom' : 'integrations.specTier.canonical')}
                              tone={field.tier === 'custom' ? 'caution' : 'neutral'}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.muted }}>
                        {field.namespace}.{field.key}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' }, order: { xs: 1, md: 0 } }}>
                      <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt }}>{field.description}</Typography>
                      {EXAMPLE.values[field.key] && (
                        <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>e.g. {EXAMPLE.values[field.key]}</Typography>
                      )}
                    </Box>
                    <Box sx={{ px: 0.75, py: 0.25, borderRadius: '6px', bgcolor: tokens.surface.tint, fontSize: 11, fontWeight: 600, color: tokens.ink.muted }}>
                      {metafieldTypeLabel(field.type)}
                    </Box>
                    {switchesOn && (
                      SWITCH_EXEMPT_KEYS.has(field.key) ? (
                        <Tooltip title={SWITCH_COPY.weightNote}>
                          <Box component="span" sx={{ fontSize: 11, color: tokens.ink.faint, textAlign: 'center', minWidth: 40 }}>Override</Box>
                        </Tooltip>
                      ) : (
                        <Switch
                          size="small"
                          checked={isFieldOn(field)}
                          disabled={switchSaving !== null}
                          onChange={() => toggleSwitch(field)}
                          slotProps={{ input: { 'aria-label': SWITCH_COPY.switchLabel(field.name) } }}
                        />
                      )
                    )}
                    {livePins ? (
                      <Tooltip title={pinTooltip(field)}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={field.pinned ? `Unpin ${field.name}` : `Pin ${field.name}`}
                            aria-pressed={field.pinned}
                            disabled={
                              pinningKey !== null
                              || !pinnable(field)
                              || (!field.pinned && atPinLimit)
                            }
                            onClick={() => togglePin(field)}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: '8px',
                              bgcolor: field.pinned ? tokens.accent.tint : 'transparent',
                              color: field.pinned ? tokens.accent.main : tokens.ink.disabled,
                            }}
                          >
                            {pinningKey === fieldId(field)
                              ? <CircularProgress size={14} />
                              : field.pinned ? <PushPin sx={{ fontSize: 16 }} /> : <PushPinOutlined sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={pinTooltip(field)}>
                        <Box
                          aria-label={field.pinned ? `Shown in Shopify Admin: ${field.name}` : `Not shown in Shopify Admin: ${field.name}`}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '8px',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: field.pinned ? tokens.accent.tint : 'transparent',
                            color: field.pinned ? tokens.accent.main : tokens.ink.disabled,
                          }}
                        >
                          {field.pinned ? <PushPin sx={{ fontSize: 16 }} /> : <PushPinOutlined sx={{ fontSize: 16 }} />}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1.25, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
            <PushPinOutlined sx={{ fontSize: 14, color: tokens.ink.faint }} />
            <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
              {livePins
                ? `Pinned fields appear on the product in Shopify Admin. Shopify allows up to ${livePins.limit}; this store has ${livePins.storePinnedCount} pinned.`
                : 'Pinned fields appear on the product in Shopify Admin. Shopify allows up to 20. Motzu pins this default set when it creates the fields.'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, flex: '1 1 320px', minWidth: 280, position: 'sticky', top: 16, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mr: 'auto' }}>Example · Sony a7 IV</Typography>
            <Box sx={{ display: 'flex', p: 0.25, bgcolor: tokens.surface.tint, borderRadius: '8px' }}>
              {(['admin', 'page'] as const).map((view) => (
                <Button
                  key={view}
                  onClick={() => setExampleView(view)}
                  sx={{
                    minWidth: 0,
                    height: 28,
                    px: 1,
                    borderRadius: '6px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: exampleView === view ? tokens.ink.primary : tokens.ink.muted,
                    bgcolor: exampleView === view ? tokens.surface.card : 'transparent',
                  }}
                >
                  {view === 'admin' ? 'Shopify Admin' : 'Product page'}
                </Button>
              ))}
            </Box>
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            {exampleView === 'admin' ? (
              <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', overflow: 'hidden' }}>
                <Typography sx={{ px: 1.5, py: 1, fontSize: 13, fontWeight: 700, bgcolor: tokens.surface.sunken }}>Metafields</Typography>
                {pinnedExamples.map((field) => (
                  <Box key={field.key} sx={{ display: 'grid', gridTemplateColumns: '42% 1fr', gap: 1, px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{field.name}</Typography>
                    <Typography sx={{ fontSize: 12 }}>{EXAMPLE.values[field.key]}</Typography>
                  </Box>
                ))}
                {pinnedCount > pinnedExamples.length && (
                  <Typography sx={{ px: 1.5, py: 0.75, fontSize: 12, color: tokens.ink.faint, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
                    + {pinnedCount - pinnedExamples.length} more under “Show all”
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', p: 1.5 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{EXAMPLE.title}</Typography>
                <Typography sx={{ fontSize: 12, color: tokens.ink.muted, my: 0.75 }}>{EXAMPLE.values.key_specs_summary}</Typography>
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, mb: 1.25, borderRadius: '6px', bgcolor: tokens.signal.positive.tint }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.signal.positive.ink }}>{EXAMPLE.values.active_rebates}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Specifications</Typography>
                {EXAMPLE.specTable.map(([label, value]) => (
                  <Box key={label} sx={{ display: 'grid', gridTemplateColumns: '38% 1fr', gap: 1, py: 0.5, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <FormDialog
        open={confirmOpen}
        title={`Create ${fields.length} fields in Shopify?`}
        onClose={closeConfirm}
        onSubmit={handleProvision}
        submitLabel={submitLabel}
        loading={provisioning}
        maxWidth="sm"
      >
        {provisionSummary && hasIssues(provisionSummary) ? (
          <Box>
            <InlineMessage severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{copy.guard.doneWithIssuesTitle}</Typography>
              <Typography variant="body2">
                {(provisionSummary.failed?.length || 0) + (provisionSummary.typeConflicts?.length || 0)} fields were not created.
              </Typography>
            </InlineMessage>
            {(provisionSummary.typeConflicts || []).map((row) => (
              <Typography key={row.key} variant="body2" sx={{ mt: 0.5 }}>{row.key}{row.existingType ? ` — ${row.existingType}` : ''}</Typography>
            ))}
            {(provisionSummary.failed || []).map((row) => (
              <Typography key={row.key} variant="body2" sx={{ mt: 0.5 }}>{row.key}{row.error ? ` — ${row.error}` : ''}</Typography>
            ))}
          </Box>
        ) : (
          <>
            {provisionError && (
              <InlineMessage severity="error">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{copy.guard.failedTitle}</Typography>
                <Typography variant="body2">{provisionError}</Typography>
              </InlineMessage>
            )}
            <Typography sx={{ fontSize: 13.5, color: tokens.ink.bodyAlt, mb: 1.5 }}>
              Motzu adds empty field labels under Settings → Custom data → Products. Products stay unchanged until the next sync, and shoppers see nothing until you add theme blocks.
            </Typography>
            <Box sx={{ px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: tokens.surface.sunken, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>{fields.length}</Box> fields under the custom namespace</Typography>
              <Typography sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>{pinnedCount}</Box> pinned to the product page in Shopify Admin</Typography>
              <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>Fields your store already has with the same key are kept as they are</Typography>
            </Box>
          </>
        )}
      </FormDialog>

      <FormDialog
        open={themeOpen}
        title={themeTitle}
        onClose={() => setThemeOpen(false)}
        onSubmit={themeOnSubmit}
        submitLabel={themeSubmit}
        submitDisabled={themeSubmitLoading || sectionBusy !== null}
        loading={themeSubmitLoading}
        hideCancel={themeHideCancel}
        maxWidth="sm"
      >
        {customAppBlocked ? (
          <InlineMessage severity="info">
            <Typography variant="body2">{themePayload?.message || t(`${SECTION_COPY}.unavailable`)}</Typography>
          </InlineMessage>
        ) : sectionsMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {scopesMissing ? (
              <InlineMessage severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t(`${SECTION_COPY}.scopesTitle`)}</Typography>
                <Typography variant="body2">{t(`${SECTION_COPY}.scopesBody`)}</Typography>
              </InlineMessage>
            ) : (
              <Typography variant="body2" color="text.secondary">{t(`${SECTION_COPY}.intro`)}</Typography>
            )}
            {sectionError && (
              <InlineMessage severity="error">
                <Typography variant="body2">{sectionError}</Typography>
              </InlineMessage>
            )}
            {sectionLinks?.metafieldWarning && (
              <InlineMessage severity="warning">
                <Typography variant="body2">{t(`${SECTION_COPY}.metafieldWarning`, { message: sectionLinks.metafieldWarning })}</Typography>
              </InlineMessage>
            )}
            {hasSectionCopy && sectionLinks && (
              <>
                <Typography variant="body2">{t(`${SECTION_COPY}.installed`, { theme: sectionLinks.themeName })}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Link href={sectionLinks.previewUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                    {t(`${SECTION_COPY}.preview`)}
                  </Link>
                  <Link href={sectionLinks.editorUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                    {t(`${SECTION_COPY}.editor`)}
                  </Link>
                </Box>
                {themePayload?.customAppSections?.updateAvailable ? (
                  <InlineMessage severity="info">{t(`${SECTION_COPY}.updateAvailable`)}</InlineMessage>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t(`${SECTION_COPY}.upToDate`)}</Typography>
                )}
                {sectionThemes.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t(`${SECTION_COPY}.chooseTheme`)}
                    value={updateThemeId}
                    onChange={(event) => setSelectedThemeId(event.target.value)}
                  >
                    {sectionThemes.map((theme) => (
                      <MenuItem key={theme.id} value={theme.id}>{theme.name}</MenuItem>
                    ))}
                  </TextField>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button variant="outlined" disabled={!updateThemeId || sectionBusy !== null} onClick={() => { void handleUpdateSections(); }}>
                    {sectionBusy === 'update' ? t(`${SECTION_COPY}.updating`) : t(`${SECTION_COPY}.update`)}
                  </Button>
                  <Button variant="text" disabled={scopesMissing || sectionBusy !== null} onClick={() => { void handleInstallSections(true); }}>
                    {sectionBusy === 'another' ? t(`${SECTION_COPY}.creating`) : t(`${SECTION_COPY}.anotherCopy`)}
                  </Button>
                </Box>
              </>
            )}
            <Typography variant="body2" color="text.secondary">{t(`${SECTION_COPY}.os2`)}</Typography>
            <Typography variant="body2" color="text.secondary">{t(`${SECTION_COPY}.compareUnplaced`)}</Typography>
            <Typography variant="body2" color="text.secondary">{t(`${SECTION_COPY}.liveUntouched`)}</Typography>
            {themeBlocks.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t(`${SECTION_COPY}.sectionsAdded`)}</Typography>
                {themeBlocks.map((block) => (
                  <Typography key={block.handle} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {block.name}
                  </Typography>
                ))}
              </Box>
            )}
            <Button variant="outlined" disabled={sectionBusy !== null} onClick={() => { void handleDownloadSections(); }} sx={{ alignSelf: 'flex-start' }}>
              {sectionBusy === 'download' ? t(`${SECTION_COPY}.downloading`) : t(`${SECTION_COPY}.download`)}
            </Button>
          </Box>
        ) : themeNotConfigured ? (
          <InlineMessage severity="info">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{copy.themeEditor.notConfiguredTitle}</Typography>
            <Typography variant="body2">{copy.themeEditor.notConfigured}</Typography>
          </InlineMessage>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{copy.themeEditor.intro}</Typography>
            {themeRequiresDefs && <InlineMessage severity="info">{copy.themeEditor.requiresDefs}</InlineMessage>}
            {themeRequiresOs2 && <InlineMessage severity="info">{copy.themeEditor.requiresOs2}</InlineMessage>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {themeBlocks.map((block) => (
                <Box key={block.handle}>
                  {block.url ? (
                    <Link href={block.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                      {block.name}
                    </Link>
                  ) : (
                    <Typography sx={{ fontWeight: 600 }}>{block.name}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{block.description}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </FormDialog>
    </>
  );
};

export default StorefrontPresetsCard;
